import { ToggleInput } from "@datagame/ui";
import { Trans, useTranslation } from "react-i18next";
import { useState } from "react";
import { useMutation } from "@apollo/client";

import type {
  FeedbackPageUpdateInput,
  Game,
  GameResultsConfigurationUpdateInput,
  WelcomePageUpdateInput,
} from "../../../../../../../gql/graphql";
import {
  FEEDBACK_PAGE_UPDATE,
  GAME_RESULTS_UPDATE,
  WELCOME_PAGE_UPDATE,
} from "../../../../../../../graphql/mutations/GameMutation";
import { Accordion } from "../../../../../../shared/ui/Accordion";
import { ActionButtons } from "../../Common/ActionButtons";
import { Container, Heading, LeftSide, RightSide, Section } from "../../Common";
import { FeedbackPageForm } from "../FeedbackPageForm";
import { GAME_ALL } from "../../../../../../../graphql/queries/GameQueries";
import type { GameResultsDataKeys } from "../GameResults";
import { GameResultsForm } from "../GameResultsForm";
import { isCardBasedGame } from "../../../../../../../graphql/helpers/CardBasedGame";
import { isWordopolisGame } from "../../../../../../../graphql/helpers/isWordopolisGame";
import { ScaleQuestionSection } from "../ScaleQuestionSection";
import { ShowPreviewForm } from "../ShowPreviewForm";
import { Step6Preview } from "../../../GamePreview/Step6Preview";
import { TooltipWithIcon } from "../../../../../../shared/ui/TooltipWithIcon";
import { WelcomeScreenForm } from "../WelcomeScreenForm";

import styles from "./Form.module.scss";

export interface ConfigChangeData {
  showWelcomePage?: boolean;
  showFeedbackPage?: boolean;
  showGameResults?: boolean;
  showRatingScale?: boolean;
}

interface FormProps {
  game: Game;
  projectId: string;
  dataKeys: GameResultsDataKeys[];
  onConfigChange: (data: ConfigChangeData) => void;
}

export const Form = ({ game, dataKeys, onConfigChange, projectId }: FormProps) => {
  const { t } = useTranslation();

  const [displayWelcomePage, setDisplayWelcomePage] = useState(game.configuration.showWelcomePage);
  const [displayFeedbackPage, setDisplayFeedbackPage] = useState(game.configuration.showFeedbackPage);
  const [displayGameResults, setDisplayGameResults] = useState(
    isCardBasedGame(game) && game.configuration.showGameResults,
  );
  const [displayScaleQuestion, setDisplayScaleQuestion] = useState(
    isWordopolisGame(game) && game.configuration.showRatingScale,
  );

  const [updateWelcomePage] = useMutation(WELCOME_PAGE_UPDATE, {
    refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
  });

  const [updateFeedbackPage] = useMutation(FEEDBACK_PAGE_UPDATE, {
    refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
  });

  const [updateGameResults] = useMutation(GAME_RESULTS_UPDATE);

  const handleChangeWelcomePage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayWelcomePage(e.target.checked);

    onConfigChange({
      showWelcomePage: e.target.checked,
    });
  };

  const handleChangeFeedbackPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayFeedbackPage(e.target.checked);

    onConfigChange({
      showFeedbackPage: e.target.checked,
    });
  };

  const handleChangeScaleQuestion = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayScaleQuestion(e.target.checked);

    onConfigChange({
      showRatingScale: e.target.checked,
    });
  };

  const handleGameResultsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayGameResults(e.target.checked);

    onConfigChange({
      showGameResults: e.target.checked,
    });
  };

  const handleWelcomePageUpdate = (data: WelcomePageUpdateInput) => {
    updateWelcomePage({
      variables: {
        welcomePageId: game?.configuration.welcomePage.id,
        data,
      },
    });
  };

  const handleFeedbackPageUpdate = (data: FeedbackPageUpdateInput) => {
    if (game && "feedbackPage" in game.configuration) {
      updateFeedbackPage({
        variables: {
          feedbackPageId: game.configuration.feedbackPage.id,
          data,
        },
      });
    }
  };

  const handleGameResultsUpdate = (data: GameResultsConfigurationUpdateInput) => {
    if (!isCardBasedGame(game)) {
      return;
    }

    updateGameResults({
      variables: {
        gameResultsId: game?.configuration.gameResults.id,
        data,
      },
    });
  };

  const showAdjustViewsSection = displayWelcomePage || displayGameResults || displayFeedbackPage;

  const toggles: {
    [key in GameResultsDataKeys]: JSX.Element;
  } = {
    showWelcomePage: (
      <ToggleInput
        label={t("project:gameResults.displayWelcomeScreen") ?? ""}
        name="welcome-screen"
        onChange={handleChangeWelcomePage}
        value={displayWelcomePage}
        tooltip={<TooltipWithIcon content={t("project:gameResults.displayWelcomeScreenTooltip")} />}
      />
    ),
    showGameResults: (
      <ToggleInput
        label={t("project:gameResults.displayGameResults") ?? ""}
        name="game-results"
        onChange={handleGameResultsChange}
        value={displayGameResults}
        tooltip={<TooltipWithIcon content={t("project:gameResults.displayGameResultsTooltip")} />}
      />
    ),
    showFeedbackPage: (
      <ToggleInput
        label={t("project:gameResults.displayFeedbackPage") ?? ""}
        name="feedback-page"
        onChange={handleChangeFeedbackPage}
        value={displayFeedbackPage}
        tooltip={<TooltipWithIcon content={t("project:gameResults.displayFeedbackPageTooltip")} />}
      />
    ),
    showRatingScale: (
      <div className={styles.toggleWrapper} key="rating-scale">
        <ToggleInput
          label={t("project:gameResults.displayFeedbackQuestion") ?? ""}
          name="feedback-question"
          onChange={handleChangeScaleQuestion}
          value={displayScaleQuestion}
          tooltip={<TooltipWithIcon content={t("project:gameResults.displayFeedbackQuestionTooltip")} />}
        />
        {displayScaleQuestion && <ScaleQuestionSection game={game} />}
      </div>
    ),
  };

  return (
    <Container>
      <RightSide>
        <Step6Preview game={game} />
      </RightSide>
      <LeftSide>
        <Heading title={{ main: t("project:gameResults.header1"), marked: t("project:gameResults.header2") }}>
          <Trans i18nKey={"project:gameResults.description"} />
        </Heading>

        <Section title={t("project:gameResults.preferences")}>
          <div className={styles.togglesWrapper}>{dataKeys.map((key) => toggles[key])}</div>
        </Section>

        {showAdjustViewsSection && (
          <Section title={t("project:gameResults.adjustCustomViews")}>
            <div className={styles.accordions}>
              {displayWelcomePage && (
                <Accordion title={t("project:gameResults.welcomeScreen")}>
                  <WelcomeScreenForm welcomePage={game.configuration.welcomePage} onUpdate={handleWelcomePageUpdate} />
                </Accordion>
              )}

              {displayGameResults && "gameResults" in game.configuration && (
                <Accordion title={t("project:gameResults.gameResults")}>
                  <GameResultsForm gameResults={game.configuration.gameResults} onUpdate={handleGameResultsUpdate} />
                </Accordion>
              )}

              {displayFeedbackPage && "feedbackPage" in game.configuration && (
                <Accordion title={t("project:gameResults.feedbackPage")}>
                  <FeedbackPageForm
                    feedbackPage={game.configuration.feedbackPage}
                    onUpdate={handleFeedbackPageUpdate}
                  />
                </Accordion>
              )}
            </div>
          </Section>
        )}

        <Section title={t("project:gameResults.others")}>
          <Accordion title={t("project:gameResults.showPreview")}>
            <ShowPreviewForm projectId={projectId} />
          </Accordion>
        </Section>

        <ActionButtons />
      </LeftSide>
    </Container>
  );
};
